import axios, { AxiosError } from 'axios';
import { Api } from '../axiosApi';

export type TStatusMensalidade = 'pendente' | 'pago' | 'cancelado' | 'atrasado';

export interface IMensalidade {
  idmensalidade: string;
  idempresa: string;
  nomeempresa: string;
  fantasiaempresa: string;
  ano: number;
  mes: number;
  valor: number;
  imposto: number;
  vencimento: string;
  datapagamento: string | null;
  formapagamento: string | null;
  observacao: string | null;
  status: 'pendente' | 'pago' | 'cancelado';
  whatsappenviadoem: string | null;
}

export interface IFiltroMensalidade {
  idempresa?: string;
  ano?: number;
  mes?: number;
  status?: TStatusMensalidade;
}

export interface IMensalidadeVencida {
  idmensalidade: string;
  idempresa: string;
  nomeempresa: string;
  fantasiaempresa: string;
  valor: number;
  vencimento: string;
  diasatraso: number;
}

export interface IMensalidadePorMes {
  mes: number;
  pago: number;
  pendente: number;
  imposto: number;
  despesa: number;
  saldo: number;
}

export interface IResumoMensalidade {
  ano: number;
  totalpagoano: number;
  totalpendenteano: number;
  totalatrasado: number;
  totalimpostoano: number;
  qtdatrasadas: number;
  vencidas: IMensalidadeVencida[];
  pormes: IMensalidadePorMes[];
  totaldespesasano: number;
  saldoano: number;
}

interface ErrorResponse {
  errors: {
    default: string;
  };
}

const tratarErro = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response) {
      return new Error(axiosError.response.data?.errors?.default || 'Erro desconhecido');
    } else if (axiosError.request) {
      return new Error('Nenhuma resposta do servidor');
    }
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const getAll = async (page = 1, limit = 20, filtro: IFiltroMensalidade = {}): Promise<{ data: IMensalidade[]; totalCount: number } | Error> => {
  try {
    const { data, headers } = await Api.get('/mensalidade', { params: { page, limit, ...filtro } });
    return {
      data,
      totalCount: Number(headers['x-total-count'] || 0),
    };
  } catch (error) {
    return tratarErro(error);
  }
};

const gerarAno = async (idempresa: string, ano: number, mesinicial: number, quantidademeses: number, valorbruto: number, imposto: number, diavencimento: number): Promise<void | Error> => {
  try {
    await Api.post('/mensalidade/gerar', { idempresa, ano, mesinicial, quantidademeses, valorbruto, imposto, diavencimento });
  } catch (error) {
    return tratarErro(error);
  }
};

const updateById = async (id: string, dados: { valor: number; vencimento: string; observacao?: string | null }): Promise<void | Error> => {
  try {
    await Api.put(`/mensalidade/${id}`, dados);
  } catch (error) {
    return tratarErro(error);
  }
};

const baixa = async (id: string, datapagamento: string, formapagamento: string): Promise<void | Error> => {
  try {
    await Api.post(`/mensalidade/${id}/baixa`, { datapagamento, formapagamento });
  } catch (error) {
    return tratarErro(error);
  }
};

const estornarBaixa = async (id: string): Promise<void | Error> => {
  try {
    await Api.post(`/mensalidade/${id}/estornar`);
  } catch (error) {
    return tratarErro(error);
  }
};

const notificarAtraso = async (id: string): Promise<void | Error> => {
  try {
    await Api.post(`/mensalidade/${id}/notificar-atraso`);
  } catch (error) {
    return tratarErro(error);
  }
};

const deleteById = async (id: string): Promise<void | Error> => {
  try {
    await Api.delete(`/mensalidade/${id}`);
  } catch (error) {
    return tratarErro(error);
  }
};

const resumo = async (ano: number): Promise<IResumoMensalidade | Error> => {
  try {
    const { data } = await Api.get('/mensalidade/resumo', { params: { ano } });
    return data;
  } catch (error) {
    return tratarErro(error);
  }
};

export const MensalidadeService = {
  getAll,
  gerarAno,
  updateById,
  baixa,
  estornarBaixa,
  deleteById,
  resumo,
  notificarAtraso,
};
